import { useState, useCallback, useEffect } from 'react';
import {
  runOIDCDebugCheck,
  formatDebugReport,
  type OIDCDebugReport,
} from './OIDCDebugger';

export interface UseOIDCDebugger {
  isRunning: boolean;
  report: OIDCDebugReport | null;
  error: string | null;
  runDebugCheck: () => Promise<void>;
  clearReport: () => void;
  formattedReport: string | null;
}

/**
 * React hook for OIDC debugging functionality
 * Provides an easy way to run debug checks from React components
 */
export function useOIDCDebugger(): UseOIDCDebugger {
  const [isRunning, setIsRunning] = useState(false);
  const [report, setReport] = useState<OIDCDebugReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runDebugCheck = useCallback(async () => {
    setIsRunning(true);
    setError(null);

    try {
      const debugReport = await runOIDCDebugCheck();
      setReport(debugReport);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An unknown error occurred',
      );
      setReport(null);
    } finally {
      setIsRunning(false);
    }
  }, []);

  const clearReport = useCallback(() => {
    setReport(null);
    setError(null);
  }, []);

  const formattedReport = report ? formatDebugReport(report) : null;

  return {
    isRunning,
    report,
    error,
    runDebugCheck,
    clearReport,
    formattedReport,
  };
}

/**
 * Hook for checking if we're in a preview environment
 */
export function useIsPreviewEnvironment(): boolean {
  const [isPreview, setIsPreview] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.location) return false;
    return window.location.hostname.includes('preview.cms-');
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.location) return;

    const checkPreview = () => {
      setIsPreview(window.location.hostname.includes('preview.cms-'));
    };

    // Check on mount and when location changes
    checkPreview();

    // Listen for location changes (if needed)
    const handleLocationChange = () => checkPreview();
    window.addEventListener('popstate', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  return isPreview;
}

/**
 * Hook for getting environment-specific API endpoints
 */
export function useOIDCEndpoints() {
  const [endpoints, setEndpoints] = useState(() => {
    if (typeof window === 'undefined' || !window.location) {
      return {
        oidcEndpoint: '/internal/jwt',
        contentEndpoint:
          'https://api.cms-test.theinstitutes.org/kontent-ai/keys-preview',
        baseApiUrl: 'https://api.cms-test.theinstitutes.org',
      };
    }

    const hostname = window.location.hostname;
    const baseApiUrl = hostname.includes('test')
      ? 'https://api.cms-test.theinstitutes.org'
      : hostname.includes('sandbox')
        ? 'https://api.cms-sandbox.theinstitutes.org'
        : 'https://api.cms-prod.theinstitutes.org';

    return {
      oidcEndpoint: `/internal/jwt`,
      contentEndpoint: `${baseApiUrl}/kontent-ai/keys-preview`,
      baseApiUrl,
    };
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.location) return;

    const updateEndpoints = () => {
      const hostname = window.location.hostname;
      const baseApiUrl = hostname.includes('test')
        ? 'https://api.cms-test.theinstitutes.org'
        : hostname.includes('sandbox')
          ? 'https://api.cms-sandbox.theinstitutes.org'
          : 'https://api.cms-prod.theinstitutes.org';

      setEndpoints({
        oidcEndpoint: `/internal/jwt`,
        contentEndpoint: `${baseApiUrl}/kontent-ai/keys-preview`,
        baseApiUrl,
      });
    };

    // Check on mount and when location changes
    updateEndpoints();

    // Listen for location changes (if needed)
    const handleLocationChange = () => updateEndpoints();
    window.addEventListener('popstate', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  return endpoints;
}
